import {useMediaQuery, useTheme} from '@mui/material';
import classNames from 'classnames';

import {AddToCartDiscountIcon} from 'assets/icons/addToCartIcon';
import {AddToCartNormalIcon} from 'assets/icons/addToCartNormal';
import {FloatingQuantityPickerWrapper} from 'components/floating-quantity-picker/FloatingQuantityPickerWrapper';
import {StyledFloatingQuantityPickerContainer} from 'components/floating-quantity-picker/styles';
import {config} from 'config/env';
import {useCounter} from 'hooks/recommendation-hooks/useCounter';
import {useFloatingQuantityPicker} from 'hooks/recommendation-hooks/useFloatingQuantityPicker';
import {useCurrentBasketItem} from 'hooks/useCurrentBasketItem';
import {useProductPriceAndText} from 'hooks/useProductPriceAndText';
import {Product} from 'types';
import {getCurrencySymbol} from 'utils';

import {
  DiscountWeightedFreshKgText,
  NoDiscountNormalPriceWrapper,
  StyledDiscountAddToBasketBtn,
  StyledDiscountOrSlabPriceContainer,
  StyledDiscountOrSlabPriceContent,
  StyledDiscountPercentageContainer,
  StyledDiscountPercentageTypography,
  StyledDiscountPrice,
  StyledDiscountPriceTypography,
  StyledDiscountStrikedPriceSymbol,
  StyledDiscountStrikedPriceSymbolTypography,
  StyledLeftTriangleIcon,
  StyledNormalPriceText,
  StyledSlabPriceText,
  StyledUpdatedQuanityTypography
} from './styles';

export type productCardLEOdiscountOrSlabPriceBoxProps = {
  product: Product;
  isShowAddtoCartBtn: boolean;
  handleClickAddToBasket: (e: React.MouseEvent<HTMLDivElement>) => void;
  productCount: number;
  isAddToCart: boolean;
  sendCarouselImpressionAnalytics: () => void;
  isPLP?: boolean;
  addAnalytics?: () => void;
  showOnlyAddToCartButton?: boolean;
};

const ProductCardLEOdiscountOrSlabPriceBox: React.FC<productCardLEOdiscountOrSlabPriceBoxProps> = ({
  product,
  isShowAddtoCartBtn,
  handleClickAddToBasket,
  productCount = 0,
  isAddToCart = false,
  isPLP = false,
  showOnlyAddToCartButton,
  sendCarouselImpressionAnalytics
}) => {
  const {
    isSlabPrice,
    slabPriceInfoText,
    discountPercentage,
    productDisplayPrice,
    kgText,
    productOriginalPrice,
    isDiscounted,
    quantity
  } = useProductPriceAndText({product, productCount, defaultSlabPriceTire: isPLP});

  const {basketItem} = useCurrentBasketItem(product);
  const classForAddToCart = isAddToCart && !isDiscounted;
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const isFloatingPickerEnabled = config.RECOM_SHOPPING_ENABLE_PRODUCT_FLOATING_QUANTITY_PICKER;

  const AddToCartIcon = showOnlyAddToCartButton ? AddToCartNormalIcon : AddToCartDiscountIcon;
  const iconSize = isMobileView || showOnlyAddToCartButton ? 28 : 32;
  const {togglePicker, showPicker, closePicker} = useFloatingQuantityPicker({
    id: `${product.id}_${product.makroId}`
  });
  const showQuantityPicker = showPicker && isFloatingPickerEnabled;

  const showKg = product?.isWeightedFresh;
  const enablePartialQuantity = showKg && product?.moq === 1 && product?.sellInPiece === false;
  const {count, inProgress, onAdd, onIncrement, onChange, onDecrement, onDelete} = useCounter({
    initialCount: basketItem?.quantity,
    enablePartialQuantity,
    product,
    closePicker,
    sendCarouselImpressionAnalytics
  });

  const renderFloatingQuantityPicker = () =>
    showQuantityPicker && (
      <StyledFloatingQuantityPickerContainer
        className={classNames({
          isDiscounted: isDiscounted,
          slabPriceInfoText: slabPriceInfoText
        })}
      >
        <FloatingQuantityPickerWrapper
          max={product.totalInventory}
          disabled={inProgress}
          onChange={onChange}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
          showKg={showKg}
          moq={product?.moq}
          isPDP={false}
          initialValue={count}
          onDelete={onDelete}
          size={'medium'}
          enablePartialQuantity={enablePartialQuantity}
        />
      </StyledFloatingQuantityPickerContainer>
    );

  const StyledDiscountOrSlabPriceContentRight = (
    <StyledDiscountAddToBasketBtn
      data-test-id={'btn_add_to_basket'}
      className={classNames({
        updatedQuantityBgColor: quantity,
        normalQuantityBgColor: quantity === 0,
        compact: showOnlyAddToCartButton
      })}
      onClick={async (e) => {
        e.stopPropagation();
        if (isFloatingPickerEnabled) {
          if (productCount === 0 && onAdd) {
            await onAdd(e);
            togglePicker();
          } else {
            togglePicker();
          }
        } else {
          handleClickAddToBasket(e);
        }
      }}
    >
      {renderFloatingQuantityPicker()}
      {quantity ? (
        <StyledUpdatedQuanityTypography
          className={classNames({
            medium: quantity?.toString().length === 4,
            small: quantity?.toString().length === 5,
            extraSmall: quantity?.toString().length > 5
          })}
        >
          {quantity}
        </StyledUpdatedQuanityTypography>
      ) : (
        <AddToCartIcon width={iconSize} height={iconSize} />
      )}
    </StyledDiscountAddToBasketBtn>
  );

  if (showOnlyAddToCartButton) {
    return StyledDiscountOrSlabPriceContentRight;
  }

  return (
    <StyledDiscountOrSlabPriceContainer
      className={classNames({
        slabPrices: isSlabPrice,
        discount: !isSlabPrice,
        noBgColorForAddToCart: classForAddToCart
      })}
    >
      <StyledDiscountOrSlabPriceContent className="left">
        {isSlabPrice && (
          <StyledSlabPriceText
            className={classNames({
              textColorForAddToCart: classForAddToCart
            })}
          >
            {slabPriceInfoText}
          </StyledSlabPriceText>
        )}
        <StyledNormalPriceText
          className={classNames({
            textColorForAddToCart: classForAddToCart,
            addToCart: isAddToCart,
            plp: isPLP
          })}
        >
          <NoDiscountNormalPriceWrapper data-test-id={`${productDisplayPrice}_lbl_price`}>
            {`${productDisplayPrice}`}
            <StyledDiscountStrikedPriceSymbolTypography className={classNames({noDiscount: !isDiscounted && !isPLP})}>
              {getCurrencySymbol()}
              {kgText && (
                <DiscountWeightedFreshKgText
                  className={classNames({noDiscount: !isDiscounted && !isPLP, plp: isPLP, addToCart: isAddToCart})}
                >
                  {kgText}
                </DiscountWeightedFreshKgText>
              )}
            </StyledDiscountStrikedPriceSymbolTypography>
          </NoDiscountNormalPriceWrapper>
        </StyledNormalPriceText>
        {(isDiscounted || isPLP) && (
          <StyledDiscountPrice className={isSlabPrice ? 'slabPriceDiscountSectionPadding' : 'discountSectionPadding'}>
            <StyledDiscountPriceTypography data-test-id={`${productOriginalPrice?.stringValue}_lbl_old_price`}>
              {productOriginalPrice?.stringValue}
            </StyledDiscountPriceTypography>
            <StyledDiscountStrikedPriceSymbol>{getCurrencySymbol()}</StyledDiscountStrikedPriceSymbol>
            <StyledDiscountPercentageContainer>
              {discountPercentage && (
                <>
                  <StyledLeftTriangleIcon />
                  <StyledDiscountPercentageTypography>-{discountPercentage}%</StyledDiscountPercentageTypography>
                </>
              )}
            </StyledDiscountPercentageContainer>
          </StyledDiscountPrice>
        )}
      </StyledDiscountOrSlabPriceContent>

      {
        <StyledDiscountOrSlabPriceContent className="right">
          {isShowAddtoCartBtn && StyledDiscountOrSlabPriceContentRight}
        </StyledDiscountOrSlabPriceContent>
      }
    </StyledDiscountOrSlabPriceContainer>
  );
};

export default ProductCardLEOdiscountOrSlabPriceBox;
